import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { iif, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { PaginatedResource } from '@shared/models/paginated-resource';
import {
  AssetManagementRequestParams,
  BalanceSheetEntry,
  BatchUpdateCategoryAccountEntry,
  CategoryAccount,
  CategoryAccountRequest,
  FinancialStatementEntry,
  GeneralLedgerEntry,
  AssetManagementFileSources,
  IncomeStatementEntry,
  RegularAccount,
  ReportPeriod,
  TrialBalanceEntry,
  FinancialStatementTypes,
  AccountMapping,
  AssetBudgetResponse,
  BaseAssetManagementRequestParams,
  PaginatedAssetManagementRequestParams,
} from '../models';
import { AccountMappingDialogData } from '../components/account-mapping-management-dialog/account-mapping-management-dialog.component';
import { ReportPeriodUtils } from '../utils/report-period.utils';
import { CategoryBalanceResponse } from '../models/category-balance';
import { TrailingIncomeStatementResponse } from '../models/trailing-income-statement';
import { AssetMetadata } from '../models/asset-metadata';
import { RelatedToType } from '@shared/enums';

@Injectable({
  providedIn: 'root',
})
export class AssetManagementService {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);

  private readonly baseAssetManagementUrl = 'asset_management';
  private readonly baseAccountingReportsUrl = `${this.baseAssetManagementUrl}/accounting_reports`;

  private readonly generalLedgerApiUrl = `${this.baseAccountingReportsUrl}/general_ledger`;
  private readonly trialBalanceApiUrl = `${this.baseAccountingReportsUrl}/trial_balance`;
  private readonly balanceSheetApiUrl = `${this.baseAccountingReportsUrl}/balance_sheet`;
  private readonly incomeStatementApiUrl = `${this.baseAccountingReportsUrl}/income_statement`;
  private readonly categoryBalancesApiUrl = `${this.baseAccountingReportsUrl}/gl_category_balances`;
  private readonly generalLedgerImportApiUrl = `${this.baseAssetManagementUrl}/gl_journal_entry_line_items/import/import_gl`;
  private readonly budgetImportApiUrl = `${this.baseAssetManagementUrl}/gl_journal_entry_line_items/import/import_budget`;
  private readonly trialBalanceImportApiUrl = `${this.baseAssetManagementUrl}/gl_journal_entry_line_items/import/import_trial_balance`;
  private readonly categoryAccountsApiUrl = `${this.baseAssetManagementUrl}/gl_categories`;
  private readonly regularAccountsApiUrl = `${this.baseAssetManagementUrl}/gl_accounts`;
  private readonly accountTreeApiUrl = `${this.baseAssetManagementUrl}/gl_trees`;
  private readonly budgetApiUrl = `${this.baseAccountingReportsUrl}/budget`;
  private readonly trailingIncomeStatementApi = `${this.baseAccountingReportsUrl}/trailing_income_statement`;

  // For now we are only using the default tree id of 1
  private readonly accountMappingApiUrl = (treeId = 1) =>
    `${this.baseAssetManagementUrl}/gl_trees/${treeId}/gl_tree_accounts`;
  private readonly importMappingApiUrl = (treeId = 1) =>
    `${this.baseAssetManagementUrl}/gl_trees/${treeId}/import_mapping`;

  private get accountId(): string {
    return this.store.selectSnapshot(AccountState.getAccountId);
  }

  getGeneralLedger(
    { page, per_page, report_period, q, sort }: AssetManagementRequestParams,
    asset: AssetMetadata,
  ): Observable<PaginatedResource<GeneralLedgerEntry>> {
    const params: PaginatedAssetManagementRequestParams = AssetManagementService.accountingReportRequestParamsFactory(
      {
        page,
        per_page,
        report_period,
        q,
        sort,
      },
      asset,
    );

    return this.httpClient.post<PaginatedResource<GeneralLedgerEntry>>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.generalLedgerApiUrl}`,
      { ...params },
    );
  }

  importGeneralLedger(selectedFile: File, asset: AssetMetadata, source: AssetManagementFileSources): Observable<void> {
    if (!selectedFile) {
      console.error('No General Ledger import file selected!');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('related_to_id', asset.related_to_id.toString());
    formData.append('related_to_type', asset.related_to_type);
    formData.append('source', source);

    return this.httpClient.post<void>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.generalLedgerImportApiUrl}`,
      formData,
    );
  }

  getTrialBalance(
    { page, per_page, report_period, sort }: AssetManagementRequestParams,
    asset: AssetMetadata,
  ): Observable<PaginatedResource<TrialBalanceEntry>> {
    const params = AssetManagementService.accountingReportRequestParamsFactory(
      {
        page,
        per_page,
        report_period,
        sort,
      },
      asset,
    );

    return this.httpClient.post<PaginatedResource<TrialBalanceEntry>>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.trialBalanceApiUrl}`,
      { ...params },
    );
  }

  importTrialBalance(selectedFile: File, asset: AssetMetadata, source: AssetManagementFileSources): Observable<void> {
    if (!selectedFile) {
      console.error('No General Ledger import file selected!');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('related_to_id', asset.related_to_id.toString());
    formData.append('related_to_type', asset.related_to_type);
    formData.append('source', source);

    return this.httpClient.post<void>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.trialBalanceImportApiUrl}`,
      formData,
    );
  }

  getCategoryBalances(
    reportPeriod: ReportPeriod,
    categoryIds: (number | string)[],
    asset: AssetMetadata,
  ): Observable<CategoryBalanceResponse> {
    const body = {
      filter: {
        ...ReportPeriodUtils.buildReportPeriod(reportPeriod),
        gl_category_ids: [...categoryIds],
      },
      related_to_type: asset.related_to_type,
    };

    return this.httpClient.post<CategoryBalanceResponse>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.categoryBalancesApiUrl}`,
      body,
    );
  }

  getAssetBudget(asset: AssetMetadata, reportPeriod: ReportPeriod): Observable<AssetBudgetResponse> {
    const report_period = ReportPeriodUtils.buildReportPeriod(reportPeriod);
    const body = AssetManagementService.baseAssetManagementRequestFactory({ report_period }, asset);

    return this.httpClient.post<AssetBudgetResponse>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.budgetApiUrl}`,
      body,
    );
  }

  importPropertyBudget(selectedFile: File, asset: AssetMetadata, source: AssetManagementFileSources): Observable<void> {
    if (!selectedFile) {
      console.error('No General Ledger import file selected!');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('related_to_id', asset.related_to_id.toString());
    formData.append('related_to_type', asset.related_to_type);
    formData.append('source', source);

    return this.httpClient.post<void>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.budgetImportApiUrl}`,
      formData,
    );
  }

  getTrailingIncomeStatement(
    asset: AssetMetadata,
    reportPeriod: ReportPeriod,
  ): Observable<TrailingIncomeStatementResponse> {
    const report_period = ReportPeriodUtils.buildReportPeriod(reportPeriod);
    const body = AssetManagementService.baseAssetManagementRequestFactory({ report_period }, asset);

    return this.httpClient.post<TrailingIncomeStatementResponse>(
      `${AssetManagementService.getAssetBaseUrl(asset)}/${this.trailingIncomeStatementApi}`,
      body,
    );
  }

  getCategoryAccounts(): Observable<CategoryAccount[]> {
    return this.httpClient
      .get<PaginatedResource<CategoryAccount>>(`accounts/${this.accountId}/${this.categoryAccountsApiUrl}`)
      .pipe(map((res: PaginatedResource<CategoryAccount>) => res.results));
  }

  createCategoryAccount(request: CategoryAccountRequest): Observable<CategoryAccount> {
    const formattedRequest: { gl_category: CategoryAccountRequest } = {
      gl_category: {
        ...request,
        parent_id: request.parent_id || null,
      },
    };

    return this.httpClient.post<CategoryAccount>(
      `accounts/${this.accountId}/${this.categoryAccountsApiUrl}`,
      formattedRequest,
    );
  }

  updateCategoryAccount(request: CategoryAccountRequest): Observable<CategoryAccount> {
    return this.httpClient.put<CategoryAccount>(
      `accounts/${this.accountId}/${this.categoryAccountsApiUrl}/${request.id}`,
      {
        gl_category: {
          ...request,
        },
      },
    );
  }

  deleteCategoryAccount(request: CategoryAccountRequest): Observable<CategoryAccount> {
    return this.httpClient.delete<CategoryAccount>(
      `accounts/${this.accountId}/${this.categoryAccountsApiUrl}/${request.id}`,
      {
        body: {
          gl_category: {
            ...request,
          },
        },
      },
    );
  }

  updateCategoryAccounts(categoryAccounts: BatchUpdateCategoryAccountEntry[]): Observable<unknown> {
    return this.httpClient.post<unknown>(`accounts/${this.accountId}/${this.categoryAccountsApiUrl}/update_order`, {
      gl_categories: [...categoryAccounts],
    });
  }

  getRegularAccounts(): Observable<RegularAccount[]> {
    return this.httpClient
      .get<PaginatedResource<RegularAccount>>(`accounts/${this.accountId}/${this.regularAccountsApiUrl}`)
      .pipe(
        map((res) =>
          res.results.map((account) => ({
            ...account,
            starting_balance: `${account.starting_balance}`, // TODO ts-table converts 0 numeric values to empty strings
          })),
        ),
      );
  }

  getBalanceSheet(reportPeriod: ReportPeriod, asset: AssetMetadata): Observable<BalanceSheetEntry[]> {
    const report_period = ReportPeriodUtils.buildReportPeriod(reportPeriod) || reportPeriod;
    const body = AssetManagementService.baseAssetManagementRequestFactory({ report_period }, asset);

    return this.httpClient
      .post<any>(`${AssetManagementService.getAssetBaseUrl(asset)}/${this.balanceSheetApiUrl}`, body)
      .pipe(
        map((res) =>
          res?.results
            // fix: BE is returning a trailing `null` value
            .filter((entry: BalanceSheetEntry) => !!entry)
            .map(AssetManagementService.moveAggregationTypesToLast),
        ),
      );
  }

  getIncomeStatement(reportPeriod: ReportPeriod, asset: AssetMetadata): Observable<IncomeStatementEntry[]> {
    const report_period = ReportPeriodUtils.buildReportPeriod(reportPeriod) || reportPeriod;
    const body = AssetManagementService.baseAssetManagementRequestFactory({ report_period }, asset);

    return this.httpClient
      .post<any>(`${AssetManagementService.getAssetBaseUrl(asset)}/${this.incomeStatementApiUrl}`, body)
      .pipe(map((res) => res?.results.map(AssetManagementService.moveAggregationTypesToLast)));
  }

  getFinancialStatement(
    asset: AssetMetadata,
    reportType: FinancialStatementTypes,
    reportPeriod?: ReportPeriod,
  ): Observable<FinancialStatementEntry[]> {
    return iif(
      () => reportType === 'balance-sheet',
      this.getBalanceSheet(reportPeriod, asset),
      this.getIncomeStatement(reportPeriod, asset),
    );
  }

  createAccountMapping(
    accountTreeId: number,
    { regular_account_id, category_account_id }: Required<AccountMappingDialogData>,
  ): Observable<void> {
    return this.httpClient.post<void>(`accounts/${this.accountId}/${this.accountMappingApiUrl(accountTreeId)}`, {
      gl_tree_account: {
        gl_tree_id: accountTreeId,
        gl_account_id: regular_account_id,
        gl_category_id: category_account_id,
      },
    });
  }

  updateAccountMapping(
    accountTreeId: number,
    { regular_account_id, category_account_id, account_mapping_id }: Required<AccountMappingDialogData>,
  ) {
    return this.httpClient.put<void>(
      `accounts/${this.accountId}/${this.accountMappingApiUrl(accountTreeId)}/${account_mapping_id}`,
      {
        gl_tree_account: {
          gl_tree_id: accountTreeId,
          gl_account_id: regular_account_id,
          gl_category_id: category_account_id,
        },
      },
    );
  }

  getAccountMappings(
    accountTreeId: number,
    params: {
      page?: number;
      per_page?: number;
      q?: string;
    },
  ): Observable<PaginatedResource<AccountMapping>> {
    return this.httpClient.get<PaginatedResource<AccountMapping>>(
      `accounts/${this.accountId}/${this.accountMappingApiUrl(accountTreeId)}`,
      { params },
    );
  }

  deleteAccountMapping(accountTreeId: number, accountMapping: AccountMapping) {
    return this.httpClient.delete<void>(
      `accounts/${this.accountId}/${this.accountMappingApiUrl(accountTreeId)}/${accountMapping.id}`,
    );
  }

  importChartOfAccounts(accountTreeId: number, file: File): Observable<void> {
    const formData = new FormData();

    formData.append('file', file);

    return this.httpClient.post<void>(
      `accounts/${this.accountId}/${this.importMappingApiUrl(accountTreeId)}`,
      formData,
    );
  }

  getAccountTrees() {
    return this.httpClient.get(`accounts/${this.accountId}/${this.accountTreeApiUrl}`);
  }

  private static baseAssetManagementRequestFactory(
    { report_period }: AssetManagementRequestParams,
    asset: AssetMetadata,
  ): BaseAssetManagementRequestParams {
    return {
      related_to_type: asset.related_to_type,
      filter: {
        ...report_period,
      },
    };
  }

  private static accountingReportRequestParamsFactory(
    { page, per_page, report_period, q, sort }: AssetManagementRequestParams,
    asset: AssetMetadata,
  ): PaginatedAssetManagementRequestParams {
    return {
      related_to_type: asset.related_to_type,
      page,
      per_page,
      filter: {
        q,
        ...report_period,
      },
      sort,
    };
  }

  private static moveAggregationTypesToLast(financialStatement: FinancialStatementEntry): FinancialStatementEntry {
    if (
      financialStatement?.display !== 'group' ||
      (financialStatement?.children && financialStatement.children.length === 0)
    )
      return financialStatement;

    const childrenWithAggregationsLast = [...financialStatement.children].sort((a, b) => {
      if (a.display === 'aggregation') return 1;
      if (b.display === 'aggregation') return -1;
      return 0;
    });

    return {
      ...financialStatement,
      children: childrenWithAggregationsLast.map(AssetManagementService.moveAggregationTypesToLast),
    };
  }

  private static getAssetBaseUrl(asset: AssetMetadata) {
    return `assets/${asset.related_to_id}`;
  }
}
